/**
 * RCAResults — assessment results page.
 *
 * Uses :id param from URL (assessment ID in context state).
 * Loads phase scores from context state + scoring engine.
 * Left: scores + heatmap + breakdown table
 * Right: AI recommendations + export + share
 */

import { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Share2, FileDown, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { PhaseHeatmap } from '@/components/charts/PhaseHeatmap';
import { RecommendationsPanel } from '@/components/results/RecommendationsPanel';
import { useRCA } from '@/context/RCAContext';
import { PHASES, PHASE_NAMES } from '@/data/rcaQuestions';
import { computePhaseCategory, computeOverall } from '@/services/rcaScoring';
import { recommend } from '@/services/aiService';
import { useGenerateShareToken } from '@/hooks/useRCAData';
import { cn } from '@/lib/utils';
import type { PhaseId, RiskLevel, RCACategory, AIRecommendation } from '@/types/rca';

// ============================================================
// Helpers
// ============================================================

function levelBadgeClass(level: RiskLevel): string {
  switch (level) {
    case 'High':
      return 'bg-risk-high/10 text-risk-high border-risk-high/30';
    case 'Medium':
      return 'bg-risk-medium/10 text-risk-medium border-risk-medium/30';
    case 'Low':
      return 'bg-risk-low/10 text-risk-low border-risk-low/30';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

function categoryBadgeClass(cat: RCACategory | null): string {
  switch (cat) {
    case 4:
      return 'bg-cat-4/15 text-cat-4 border-cat-4/40';
    case 3:
      return 'bg-cat-3/15 text-cat-3 border-cat-3/40';
    case 2:
      return 'bg-cat-2/15 text-cat-2 border-cat-2/40';
    case 1:
      return 'bg-cat-1/15 text-cat-1 border-cat-1/40';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

function ScoreDisplay({
  label,
  score,
  level,
}: {
  label: string;
  score: number | null;
  level: RiskLevel;
}) {
  return (
    <div className="text-center space-y-1">
      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="text-4xl font-bold text-foreground tabular-nums">
        {score != null ? Math.round(score) : '—'}
      </p>
      <Badge
        variant="outline"
        className={cn('text-xs font-semibold', levelBadgeClass(level))}
      >
        {level}
      </Badge>
    </div>
  );
}

// ============================================================
// Main Component
// ============================================================

export default function RCAResults() {
  const { id } = useParams<{ id: string }>();
  const { state } = useRCA();
  const generateShareToken = useGenerateShareToken();

  const [recommendations, setRecommendations] = useState<AIRecommendation[]>([]);
  const [recsLoading, setRecsLoading] = useState(false);
  const [recsError, setRecsError] = useState<string | null>(null);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);

  const activePhases = state.activePhases;
  const answers = state.answers;

  // Compute phase-level results
  const phaseResults = activePhases.map((phaseId) => {
    const cat = computePhaseCategory(phaseId, activePhases, answers, PHASES);
    return { phaseId, ...cat };
  });

  const overall = computeOverall(activePhases, answers, PHASES);

  // Fire AI recommendations on mount
  useEffect(() => {
    if (!overall.category) return;

    setRecsLoading(true);
    setRecsError(null);

    const project = {
      id: state.assessmentId,
      name: state.project || 'Untitled Project',
      department: state.department,
      status: 'active',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const phaseResultsMap = Object.fromEntries(
      phaseResults.map((r) => [r.phaseId, {
        avgRisk: r.avgRisk,
        avgComplexity: r.avgComplexity,
        riskLevel: r.riskLevel,
        complexityLevel: r.complexityLevel,
        category: r.category,
      }])
    ) as Record<PhaseId, { avgRisk: number | null; avgComplexity: number | null; riskLevel: RiskLevel; complexityLevel: RiskLevel; category: RCACategory | null }>;

    recommend(
      {
        assessmentId: state.assessmentId,
        project: state.project,
        department: state.department,
        assessor: state.assessor,
        assessorEmail: state.assessorEmail,
        activePhases,
        answers,
        phaseResults: phaseResultsMap,
        overall,
        status: state.status,
      },
      project,
    )
      .then((recs) => setRecommendations(recs))
      .catch((err: unknown) => {
        const msg = err instanceof Error ? err.message : 'Failed to load recommendations';
        setRecsError(msg);
      })
      .finally(() => setRecsLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Generate share link
  async function handleShare() {
    const expiresAt = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString();
    try {
      const token = await generateShareToken.mutateAsync({
        assessmentId: state.assessmentId,
        expiresAt,
      });
      const url = `${window.location.origin}/share/${token.token}`;
      setShareUrl(url);
      setShareDialogOpen(true);
    } catch {
      // Fallback — just show the current URL
      setShareUrl(window.location.href);
      setShareDialogOpen(true);
    }
  }

  async function handleExport() {
    const element = reportRef.current;
    if (!element) return;

    const { default: html2canvas } = await import('html2canvas');
    const { jsPDF } = await import('jspdf');

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
    });

    const imgData = canvas.toDataURL('image/jpeg', 0.95);
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 0.5;
    const imgW = pageW - margin * 2;
    const imgH = (canvas.height / canvas.width) * imgW;

    let y = margin;
    let remainH = imgH;
    const pageImgH = pageH - margin * 2;

    while (remainH > 0) {
      pdf.addImage(imgData, 'JPEG', margin, y, imgW, imgH);
      remainH -= pageImgH;
      if (remainH > 0) {
        pdf.addPage();
        y = margin - (imgH - remainH);
      }
    }

    const projectName = state.project ? state.project.replace(/\s+/g, '-').toLowerCase() : 'assessment';
    pdf.save(`rca-report-${projectName}.pdf`);
  }

  const assessmentDate = state.savedAt
    ? new Date(state.savedAt).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  // Build heatmap data
  const heatmapPhases = phaseResults.map((r) => ({
    phaseId: r.phaseId,
    riskLevel: r.riskLevel,
    complexityLevel: r.complexityLevel,
    category: r.category ?? null,
  }));

  return (
    <div className="min-h-screen bg-background">
      <div ref={reportRef} className="max-w-7xl mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="space-y-4">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>

          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
            <div className="space-y-1">
              <h1 className="text-2xl font-bold text-foreground">
                {state.project || 'Assessment Results'}
              </h1>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                {state.department && <span>{state.department}</span>}
                {state.assessor && <span>Assessor: {state.assessor}</span>}
                <span>{assessmentDate}</span>
                {(id ?? state.assessmentId) && (
                  <span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
                    {id ?? state.assessmentId}
                  </span>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                onClick={() => void handleShare()}
                className="gap-1.5"
              >
                <Share2 className="w-4 h-4" />
                Share
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleExport}
                className="gap-1.5"
              >
                <FileDown className="w-4 h-4" />
                Export PDF
              </Button>
            </div>
          </div>
        </div>

        <Separator />

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* ---- LEFT COLUMN ---- */}
          <div className="space-y-6">
            {/* Overall score card */}
            <Card>
              <CardHeader className="pb-2">
                <p className="text-sm font-semibold text-muted-foreground">Overall Assessment</p>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Score displays */}
                <div className="grid grid-cols-2 gap-6">
                  <ScoreDisplay
                    label="Risk Score"
                    score={overall.avgRisk}
                    level={overall.riskLevel}
                  />
                  <ScoreDisplay
                    label="Complexity Score"
                    score={overall.avgComplexity}
                    level={overall.complexityLevel}
                  />
                </div>

                {/* Category badge */}
                <div className="flex flex-col items-center gap-2 pt-2 border-t border-border">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                    Project Category
                  </p>
                  {overall.category ? (
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-base font-bold px-6 py-2',
                        categoryBadgeClass(overall.category),
                      )}
                    >
                      Category {overall.category}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-base px-6 py-2 text-muted-foreground">
                      Not Scored
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Phase heatmap */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Phase Breakdown</h3>
              <PhaseHeatmap phases={heatmapPhases} />
            </div>

            {/* Score breakdown table */}
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-foreground">Score Detail</h3>
              <div className="rounded-lg border border-border overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-muted/50">
                      <TableCell className="font-semibold text-muted-foreground text-xs uppercase">Phase</TableCell>
                      <TableCell className="font-semibold text-muted-foreground text-xs uppercase text-right">Risk</TableCell>
                      <TableCell className="font-semibold text-muted-foreground text-xs uppercase text-right">Complexity</TableCell>
                      <TableCell className="font-semibold text-muted-foreground text-xs uppercase text-center">Cat</TableCell>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {phaseResults.map((r) => (
                      <TableRow key={r.phaseId} className="hover:bg-muted/30">
                        <TableCell className="text-sm font-medium text-foreground">
                          {PHASE_NAMES[r.phaseId]}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.avgRisk != null ? (
                            <span className="text-sm">
                              {Math.round(r.avgRisk)}{' '}
                              <Badge
                                variant="outline"
                                className={cn('text-xs ml-1', levelBadgeClass(r.riskLevel))}
                              >
                                {r.riskLevel}
                              </Badge>
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {r.phaseId === 'event_driven' ? (
                            <span className="text-xs text-muted-foreground italic">Uses Planning</span>
                          ) : r.avgComplexity != null ? (
                            <span className="text-sm">
                              {Math.round(r.avgComplexity)}{' '}
                              <Badge
                                variant="outline"
                                className={cn('text-xs ml-1', levelBadgeClass(r.complexityLevel))}
                              >
                                {r.complexityLevel}
                              </Badge>
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {r.category ? (
                            <Badge
                              variant="outline"
                              className={cn('text-xs font-bold', categoryBadgeClass(r.category))}
                            >
                              {r.category}
                            </Badge>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          </div>

          {/* ---- RIGHT COLUMN ---- */}
          <div className="space-y-6">
            {/* Error for recommendations */}
            {recsError && (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {recsError}
              </div>
            )}

            <RecommendationsPanel
              recommendations={recommendations}
              isLoading={recsLoading}
            />
          </div>
        </div>
      </div>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onOpenChange={setShareDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <p className="text-base font-semibold">Share Assessment</p>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Anyone with this link can view the assessment results. Link expires in 7 days.
            </p>
            {shareUrl && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={shareUrl}
                  className="flex-1 h-9 rounded-lg border border-input bg-muted px-3 text-sm font-mono text-muted-foreground truncate"
                />
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => void navigator.clipboard.writeText(shareUrl)}
                >
                  Copy
                </Button>
                <a
                  href={shareUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex"
                >
                  <Button size="icon" variant="outline" className="h-9 w-9">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
